import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Check, X } from 'lucide-react';
import { User, PlanType, GlobalPricing, BillingProfile } from '../../types';
import { api } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { BillingProfileForm } from './BillingProfileForm';
import { PaymentHistoryTable } from './PaymentHistoryTable';
import { BillingValidationModal } from './BillingValidationModal';

interface BillingViewProps {
    user: User;
    onUpgrade: (plan: PlanType, price: number, cycle: string, taxRate: number) => void;
    onCancel: () => void;
}

export const BillingView: React.FC<BillingViewProps> = ({ user, onUpgrade, onCancel }) => {
    const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
    const [pricing, setPricing] = useState<GlobalPricing | null>(null);
    const [billingProfile, setBillingProfile] = useState<BillingProfile | undefined>(undefined);
    const [showValidationModal, setShowValidationModal] = useState(false);

    const { t } = useTranslation('billing');

    useEffect(() => {
        api.getGlobalPricing().then(setPricing);
        api.getBillingProfiles(user.id).then(profiles => {
            if (profiles.length > 0) setBillingProfile(profiles[0]);
        });
    }, [user.id]);

    const getPrice = (plan: PlanType) => {
        if (!pricing) return cycle === 'monthly' ? (plan === PlanType.LIGHT ? 19 : plan === PlanType.ADVANCED ? 49 : 199) : (plan === PlanType.LIGHT ? 190 : plan === PlanType.ADVANCED ? 490 : 1990);
        return cycle === 'monthly' ? pricing[plan].monthly : pricing[plan].yearly;
    };

    const plans = [
        { id: PlanType.LIGHT, name: t('plans.light.name'), price: getPrice(PlanType.LIGHT), description: t('plans.light.description'), features: ['customizable_widget', 'manual_faqs', 'email_support', 'remove_branding', 'custom_button', 'analytics_reporting'], missing: ['google_analytics', 'appointments', 'priority_support', 'agency_tools'] },
        { id: PlanType.ADVANCED, name: t('plans.advanced.name'), price: getPrice(PlanType.ADVANCED), description: t('plans.advanced.description'), features: ['everything_light', 'priority_24_7_support', 'google_analytics', 'appointments', 'priority_support'], missing: ['agency_tools'] },
        { id: PlanType.AGENCY, name: t('plans.agency.name'), price: getPrice(PlanType.AGENCY), description: t('plans.agency.description'), features: ['everything_advanced', '5_clients', 'client_dashboard', 'white_labeling'], missing: [] }
    ];

    const isActive = user.subscriptionStatus === 'active';
    const isCancelled = user.subscriptionStatus === 'cancelled';
    const isExpired = user.subscriptionStatus === 'expired';

    const handleUpgradeClick = (planId: PlanType, price: number, cycle: string) => {
        // Validation: Enforce Billing Profile
        const isTaxable = !billingProfile || billingProfile.country === 'GR' || billingProfile.country === 'Greece' || billingProfile.country === 'EU' || (billingProfile.type === 'individual' && billingProfile.country !== 'NON_EU' && billingProfile.country !== 'US');

        if (!billingProfile || !billingProfile.name || !billingProfile.address || !billingProfile.country) {
            setShowValidationModal(true);
            return;
        }
        onUpgrade(planId, price, cycle, isTaxable ? 0.24 : 0);
    };

    const isPendingCancel = user.cancel_at_period_end;

    const statusColor = isActive ? (isPendingCancel ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700') : isCancelled ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700';

    return (
        <div className="max-w-4xl mx-auto space-y-10">

            {/* Current Subscription Card */}
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex-1">
                    <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">{t('current_subscription')}</h2>
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-bold text-slate-900 capitalize">{t('plan_title', { plan: user.plan })}</h1>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${statusColor}`}>
                            {isPendingCancel ? t('cancels_soon') : (t(`status.${user.subscriptionStatus}`) || user.subscriptionStatus)}
                        </span>
                    </div>
                    <p className="text-slate-500 text-sm">
                        {isActive && !isPendingCancel ? t('renews_on') : t('access_ends')}
                        <span className="font-bold text-slate-800">{formatDate(user.subscriptionEnd, user.dateFormat)}</span>
                    </p>
                </div>

                <div className="flex flex-col gap-2 w-full md:w-auto items-end">
                    {/* Show Next Payment info only if Active and NOT cancelling */}
                    {isActive && !isPendingCancel && (
                        <div className="bg-slate-50 px-5 py-3 rounded-xl border border-slate-100 text-right w-full md:w-auto">
                            <p className="text-xs text-slate-500 font-bold uppercase mb-1">{t('next_payment')}</p>
                            <p className="font-mono font-bold text-slate-900 flex items-center justify-end gap-2">
                                <Calendar size={14} className="text-slate-400" />
                                {formatDate(user.subscriptionEnd, user.dateFormat)}
                            </p>
                        </div>
                    )}

                    {isActive && !isPendingCancel && (
                        <button
                            onClick={onCancel}
                            className="text-red-500 hover:text-red-700 text-xs font-bold mt-2 hover:underline px-2"
                        >
                            {t('cancel_subscription')}
                        </button>
                    )}

                    {(isCancelled || isPendingCancel) && !isExpired && (
                        <div className="text-xs text-orange-600 font-medium max-w-xs text-right bg-orange-50 p-2 rounded-lg border border-orange-100">
                            {t('subscription_cancelled_message')}
                        </div>
                    )}
                </div>
            </div>

            {/* 
            <div className="border-t border-slate-200"></div>

            <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-slate-900 mb-3">{t('available_plans')}</h2>
                <div className="mt-4 inline-flex bg-slate-100 p-1  rounded-xl relative">
                    <button onClick={() => setCycle('monthly')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${cycle === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{t('monthly')}</button>
                    <button onClick={() => setCycle('yearly')} className={`px-6 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${cycle === 'yearly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{t('yearly')} <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full uppercase tracking-wide">{t('discount_label')}</span></button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => {
                    const planWeights = { [PlanType.FREE]: 0, [PlanType.LIGHT]: 1, [PlanType.ADVANCED]: 2, [PlanType.AGENCY]: 3 };
                    const currentWeight = planWeights[user.plan] || 0;
                    const targetWeight = planWeights[plan.id] || 0;
                    const actionLabel = targetWeight > currentWeight ? t('upgrade_to', { plan: plan.name }) : t('downgrade_to', { plan: plan.name });

                    return (
                        <div key={plan.id} className={`relative p-6 rounded-3xl border-2 transition-all flex flex-col ${user.plan === plan.id ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-xl'}`}>
                            <div className="mb-4"><h3 className="text-xl font-bold text-slate-900">{plan.name}</h3></div>
                            <div className="mb-6">
                                {(() => {
                                    const isTaxable = !billingProfile || billingProfile.country === 'GR' || billingProfile.country === 'Greece' || billingProfile.country === 'EU' || (billingProfile.type === 'individual' && billingProfile.country !== 'NON_EU' && billingProfile.country !== 'US');
                                    const basePrice = plan.price; // Price is Net

                                    if (isTaxable) {
                                        const vatAmount = basePrice * 0.24;
                                        const totalPrice = basePrice + vatAmount;
                                        return (
                                            <>
                                                <div className="flex items-baseline gap-1"><span className="text-4xl font-bold text-slate-900">{formatCurrency(totalPrice, 'EUR')}</span><span className="text-slate-500 font-medium">/{cycle === 'monthly' ? 'mo' : 'yr'}</span></div>
                                                <div className="text-xs text-slate-500 mt-1 font-medium">{t('vat_inc', { net: formatCurrency(basePrice, 'EUR'), vat: formatCurrency(vatAmount, 'EUR') })}</div>
                                            </>
                                        );
                                    } else {
                                        return (
                                            <>
                                                <div className="flex items-baseline gap-1"><span className="text-4xl font-bold text-slate-900">{formatCurrency(basePrice, 'EUR')}</span><span className="text-slate-500 font-medium">/{cycle === 'monthly' ? 'mo' : 'yr'}</span></div>
                                                <div className="text-xs text-slate-500 mt-1 font-medium">{t('vat_does_not_apply')}</div>
                                            </>
                                        );
                                    }
                                })()}
                            </div>
                            <ul className="space-y-3 mb-8 flex-1">
                                {plan.features.map((f, i) => <li key={i} className="text-sm text-slate-600 flex items-center gap-2"><Check size={16} className="text-green-500 shrink-0" /> {t(`features.${f}`)}</li>)}
                                {plan.missing.map((f, i) => <li key={i} className="text-sm text-slate-400 flex items-center gap-2 line-through"><X size={16} className="shrink-0" /> {t(`features.${f}`)}</li>)}
                            </ul>
                            <button onClick={() => user.plan !== plan.id && handleUpgradeClick(plan.id, plan.price, cycle)} disabled={user.plan === plan.id} className={`w-full py-3 rounded-xl font-bold transition ${user.plan === plan.id ? 'bg-slate-200 text-slate-500 cursor-default' : 'bg-black text-white hover:bg-slate-800 shadow-lg hover:shadow-xl'}`}>{user.plan === plan.id ? t('active_plan') : `${actionLabel}`}</button>
                        </div>
                    );
                })}
            </div>
            */}

            <div id="billing-form-section">
                <BillingProfileForm user={user} currentProfile={billingProfile} onSave={() => api.getBillingProfiles(user.id).then(p => p.length && setBillingProfile(p[0]))} />
            </div>
            <PaymentHistoryTable user={user} />

            <BillingValidationModal
                isOpen={showValidationModal}
                onClose={() => {
                    setShowValidationModal(false);
                    document.getElementById('billing-form-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
            />
        </div>
    );
};
